import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Pencil, Filter, Search, FileSpreadsheet, Download, Calendar, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { formatRupiah, formatCompactRupiah, formatDate, formatDateTime, statusClass, alamatTujuanPO } from "../lib/format";
import { Section, EmptyState, Skeleton, Pagination, usePagination } from "../components/UI";

const STATUS_OPTS = ["Semua", "Draft PO", "PO Terkirim", "PO Diproses", "Dalam Pengiriman", "Barang Diterima", "Selesai", "Dibatalkan"];

export default function PurchaseOrderListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("Semua");
  const [search, setSearch] = useState("");
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [dateError, setDateError] = useState("");

  const showDateError = useCallback((msg) => {
    setDateError(msg);
    setTimeout(() => setDateError(""), 4000);
  }, []);

  const handleDateStart = (val) => {
    if (val && dateEnd && val > dateEnd) {
      showDateError("Tanggal awal tidak boleh melebihi tanggal akhir");
      setDateStart("");
      return;
    }
    setDateError("");
    setDateStart(val);
    setPage(1);
  };

  const handleDateEnd = (val) => {
    if (val && dateStart && val < dateStart) {
      showDateError("Tanggal akhir tidak boleh kurang dari tanggal awal");
      setDateEnd("");
      return;
    }
    setDateError("");
    setDateEnd(val);
    setPage(1);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/purchase-orders");
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => {
    if (filter !== "Semua" && r.status !== filter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const matchPo = r.po_number?.toLowerCase().includes(q);
      const matchSupplier = r.supplier_nama?.toLowerCase().includes(q);
      const matchSppg = r.sppg_name?.toLowerCase().includes(q);
      if (!matchPo && !matchSupplier && !matchSppg) return false;
    }
    if (dateStart) {
      const d = r.created_at?.slice(0, 10);
      if (!d || d < dateStart) return false;
    }
    if (dateEnd) {
      const d = r.created_at?.slice(0, 10);
      if (!d || d > dateEnd) return false;
    }
    return true;
  });
  const { page, setPage, perPage, changePerPage, totalPages, paged, totalItems } = usePagination(filtered, 10);
  const isSppg = user.role === "SPPG";

  const exportCsv = () => {
    if (filtered.length === 0) return;
    const headers = [
      "No. PO", "Tanggal", "SPPG", "Instansi SPPG", "Supplier", "Kota Tujuan", "Alamat Tujuan",
      "PIC SPPG", "Jabatan PIC", "No. Telp SPPG", "Total", "Status", "Alasan Pembatalan", "Waktu Pembatalan",
    ];
    const rowsCsv = filtered.map((po) => [
      po.po_number,
      new Date(po.created_at).toISOString().split("T")[0],
      po.sppg_name || "",
      po.dest_instansi || "",
      po.supplier_nama || "",
      po.dest_kota || "",
      alamatTujuanPO(po),
      po.dest_pic || "",
      po.dest_pic_jabatan || "",
      po.dest_telp || "",
      po.total || 0,
      po.status || "",
      po.status === "Dibatalkan" ? (po.alasan_batal || "") : "",
      po.status === "Dibatalkan" ? (po.dibatalkan_pada || po.updated_at || "") : "",
    ]);
    const csvContent = [headers, ...rowsCsv].map(e => e.map(item => `"${String(item).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `purchase-orders-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportPdf = () => {
    window.print();
  };

  return (
    <div className="space-y-6" data-testid="po-list-page">
      {/* ── Screen-only view (hidden when printing) ── */}
      <div className="print:hidden">
        <header className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="kedan-label text-slate-500">Procurement</div>
            <h1 className="text-3xl font-display font-bold text-slate-900 mt-1">Purchase Orders</h1>
            <p className="text-slate-500 text-sm mt-1">
              {isSppg ? "Daftar PO yang Anda buat dan statusnya." : "Daftar semua Purchase Order pada sistem."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportCsv} className="kedan-btn-secondary" data-testid="export-csv">
              <FileSpreadsheet className="w-4 h-4" /> Ekspor CSV
            </button>
            <button onClick={exportPdf} className="kedan-btn-secondary" data-testid="export-pdf">
              <Download className="w-4 h-4" /> Ekspor PDF
            </button>
            {isSppg && (
              <button onClick={() => navigate("/po-buat")} className="kedan-btn-primary ml-2" data-testid="create-po-btn">
                Buat PO
              </button>
            )}
          </div>
        </header>

        <div className="kedan-card overflow-hidden mt-6">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              className="kedan-input !w-44 !py-1.5 text-xs"
              value={filter}
              onChange={(e) => { setFilter(e.target.value); setPage(1); }}
              data-testid="filter-status"
            >
              {STATUS_OPTS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <div className="flex items-center gap-1.5 ml-auto">
              <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <input type="date" className={`kedan-input !w-36 !py-1.5 text-xs ${dateError ? "!border-red-300" : ""}`} value={dateStart} onChange={(e) => handleDateStart(e.target.value)} data-testid="filter-date-start" />
              <span className="text-xs text-slate-400">—</span>
              <input type="date" className={`kedan-input !w-36 !py-1.5 text-xs ${dateError ? "!border-red-300" : ""}`} value={dateEnd} onChange={(e) => handleDateEnd(e.target.value)} data-testid="filter-date-end" />
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              <input
                type="text"
                className="kedan-input !w-48 !py-1.5 !pl-8 text-xs"
                placeholder="Cari PO, Supplier, SPPG..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                data-testid="search-po"
              />
            </div>
            <div className="text-xs text-slate-500">{filtered.length} PO</div>
          </div>

          {/* Date validation notification */}
          {dateError && (
            <div className="mx-5 mt-3 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs font-medium animate-[fadeIn_0.2s_ease-out]" data-testid="date-error">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {dateError}
            </div>
          )}

          {loading ? (
            <div className="p-6 space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
          ) : filtered.length === 0 ? (
            <EmptyState title="Tidak ada Purchase Order" description="Belum ada PO yang sesuai filter." />
          ) : (<>
            <table className="kedan-table">
              <thead>
                <tr>
                  <th>No. PO</th>
                  <th>Tanggal</th>
                  <th>{isSppg ? "Supplier" : "SPPG"}</th>
                  <th>{isSppg ? "Tujuan" : "Supplier"}</th>
                  <th className="text-right">Total</th>
                  <th>Status</th>
                  <th className="text-right">Aksi</th>
                </tr>
              </thead>
              <tbody data-testid="po-table-body">
                {paged.map((po) => (
                  <tr key={po.id}>
                    <td className="font-mono text-xs">{po.po_number}</td>
                    <td>{formatDate(po.created_at)}</td>
                    <td>{isSppg ? po.supplier_nama : po.sppg_name}</td>
                    <td>{isSppg ? (po.dest_kota || "-") : po.supplier_nama}</td>
                    <td className="text-right font-mono" title={formatRupiah(po.total)}>{formatCompactRupiah(po.total)}</td>
                    <td>
                      <span className={`kedan-badge ${statusClass(po.status)}`}>{po.status}</span>
                      {po.status === "Dibatalkan" && (
                        <div className="mt-1 max-w-[220px] text-[11px] leading-tight" data-testid={`batal-info-${po.id}`}>
                          <div className="text-slate-600 truncate" title={po.alasan_batal || ""}>
                            {po.alasan_batal || "Tanpa keterangan"}
                          </div>
                          <div className="text-slate-400">{formatDateTime(po.dibatalkan_pada || po.updated_at)}</div>
                        </div>
                      )}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      {isSppg && po.status === "Draft PO" && (
                        <button onClick={() => navigate(`/po-edit/${po.id}`)}
                          className="p-1.5 hover:bg-slate-100 rounded text-slate-600 inline-flex items-center gap-1 text-xs"
                          data-testid={`edit-po-${po.id}`}>
                          <Pencil className="w-4 h-4" /> Ubah
                        </button>
                      )}
                      <button onClick={() => navigate(`/purchase-orders/${po.id}`)}
                        className="p-1.5 hover:bg-slate-100 rounded text-slate-600 inline-flex items-center gap-1 text-xs"
                        data-testid={`view-po-${po.id}`}>
                        <Eye className="w-4 h-4" /> Detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} totalPages={totalPages} totalItems={totalItems} perPage={perPage} onPageChange={setPage} onPerPageChange={changePerPage} />
          </>)}
        </div>
      </div>

      {/* ── Print-only view: clean full table for PDF export ── */}
      <div className="hidden print:block">
        <h1 className="text-xl font-bold text-slate-900 mb-1">Laporan Purchase Orders</h1>
        <p className="text-xs text-slate-500 mb-4">Diekspor pada: {new Date().toLocaleDateString("id-ID", { dateStyle: "long" })} · Total: {filtered.length} PO</p>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #334155" }}>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>No</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>No. PO</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>Tanggal</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>SPPG</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>Supplier</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>Tujuan</th>
              <th style={{ textAlign: "right", padding: "6px 8px" }}>Total</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((po, idx) => (
              <tr key={po.id} style={{ borderBottom: "1px solid #E2E8F0" }}>
                <td style={{ padding: "5px 8px" }}>{idx + 1}</td>
                <td style={{ padding: "5px 8px", fontFamily: "monospace", fontSize: "10px" }}>{po.po_number}</td>
                <td style={{ padding: "5px 8px" }}>{formatDate(po.created_at)}</td>
                <td style={{ padding: "5px 8px" }}>{po.sppg_name || "-"}</td>
                <td style={{ padding: "5px 8px" }}>{po.supplier_nama || "-"}</td>
                <td style={{ padding: "5px 8px" }}>
                  {po.dest_instansi || po.dest_kota || "-"}
                  {alamatTujuanPO(po) && (
                    <div style={{ fontSize: "9px", color: "#64748B" }}>
                      {alamatTujuanPO(po)}
                      {po.dest_pic && <> · PIC: {po.dest_pic}</>}
                      {po.dest_telp && <> · {po.dest_telp}</>}
                    </div>
                  )}
                </td>
                <td style={{ padding: "5px 8px", textAlign: "right", fontFamily: "monospace" }}>{formatRupiah(po.total)}</td>
                <td style={{ padding: "5px 8px" }}>
                  {po.status}
                  {po.status === "Dibatalkan" && (
                    <div style={{ fontSize: "9px", color: "#64748B" }}>
                      {po.alasan_batal || "Tanpa keterangan"} · {formatDateTime(po.dibatalkan_pada || po.updated_at)}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

